import React, { FC, useState, useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  SendIntroEmailResponse,
  CreateReplyDraftResponse,
  GetGoogleLinkedAccountsResponse,
  GoogleLinkedAccount,
} from '@dreamai/types';
import { ReactComponent as ArrowCircleRight } from '../../../icons/arrow-right-circle.svg';
import { ReactComponent as ArrowRightIcon } from '../../../icons/arrow-right.svg';
import { ReactComponent as ActionEmailSendIcon } from '../../../icons/action-email-send.svg';
import { ReactComponent as PlusAddIcon } from '../../../icons/plus-add.svg';
import api from '../../../api';
import { OnboardingStepProps } from '../OnboardingFlow';
import OnboardingTaskModal from './OnboardingTaskModal';
import './email-intro-step.scss';

type Phase = 'initial' | 'emailSent' | 'draftCreated';

const TUTORIAL_STEPS = [
  {
    title: 'Let me tell you about tasks',
    description:
      'When you need something, make a task. They make sure you get the result you need.',
  },
  {
    title: 'What do you need?',
    description:
      "Let me know what needs done. I can manage most tasks, but for now let's send an email.",
  },
  {
    title: 'Quick choices',
    description:
      "I'll use a choice chip when I need information, or want you to choose. This could be things I've saved in memory, or emails I know about.",
  },
  {
    title: 'Taking actions',
    description:
      "When you make your own tasks you choose the outcome. You may ask me to 'find a plumber', but prefer to see options before I email anyone.",
  },
  {
    title: 'All ready',
    description: "When you're ready choose 'Create Task' to get started.",
  },
];

interface EmailIntroStepProps extends OnboardingStepProps {
  assistantName: string | null;
  assistantNickname: string | null;
  assistantEmail: string | null;
  assistantEmailReady: boolean;
  assistantSignOff: string | null;
  userName: string | null;
}

const EmailIntroStep: FC<EmailIntroStepProps> = ({
  refreshStatus,
  assistantName,
  assistantNickname,
  assistantEmail,
  assistantEmailReady,
  assistantSignOff,
  userName,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [phase, setPhase] = useState<Phase>('initial');
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [tutorialStep, setTutorialStep] = useState<number | null>(null);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [gmailDraftLink, setGmailDraftLink] = useState<string | null>(null);

  // Connected Google accounts for draft creation
  const [linkedAccounts, setLinkedAccounts] = useState<GoogleLinkedAccount[]>(
    [],
  );
  const [selectedAccountEmail, setSelectedAccountEmail] = useState<
    string | null
  >(null);

  const emailAvailable = !!assistantEmail && assistantEmailReady;

  // Fetch the user's connected Google accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<GetGoogleLinkedAccountsResponse>(
          '/google-link',
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setLinkedAccounts(res.data.linkedAccounts);
        if (res.data.linkedAccounts.length > 0 && !selectedAccountEmail) {
          setSelectedAccountEmail(res.data.linkedAccounts[0].email);
        }
      } catch (err) {
        console.error('[EmailIntro] Error fetching linked accounts:', err);
      }
    };
    fetchAccounts();
  }, [getAccessTokenSilently]);

  // Send the intro email from the assistant
  const handleSendEmail = useCallback(async () => {
    if (sending) return;
    setSending(true);
    try {
      const token = await getAccessTokenSilently();
      await api.post<SendIntroEmailResponse>(
        '/onboarding/send-intro-email',
        { recipientEmail: selectedAccountEmail || undefined },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShowSendModal(false);
      setPhase('emailSent');
    } catch (err) {
      console.error('[EmailIntro] Failed to send intro email:', err);
    } finally {
      setSending(false);
    }
  }, [getAccessTokenSilently, sending, selectedAccountEmail]);

  // Create a draft reply in the user's Gmail
  const handleCreateDraft = useCallback(async () => {
    if (drafting || !selectedAccountEmail) return;
    setDrafting(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.post<CreateReplyDraftResponse>(
        '/onboarding/create-reply-draft',
        { oauthEmail: selectedAccountEmail },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success && res.data.gmailDraftLink) {
        setGmailDraftLink(res.data.gmailDraftLink);
      }
      setShowDraftModal(false);
      setPhase('draftCreated');
    } catch (err) {
      console.error('[EmailIntro] Failed to create reply draft:', err);
    } finally {
      setDrafting(false);
    }
  }, [getAccessTokenSilently, drafting, selectedAccountEmail]);

  // Complete the step and proceed
  const handleNext = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      await api.post('/onboarding/complete-contact-step', null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      sessionStorage.removeItem('onboarding_assistant_image');
      await refreshStatus();
    } catch (err) {
      console.error('[EmailIntro] Failed to complete contact step:', err);
    }
  }, [getAccessTokenSilently, refreshStatus]);

  const displayName = assistantName || 'Your Assistant';
  const displayNickname = assistantNickname || displayName;
  const displayEmail = assistantEmail || '';
  const displaySignOff = assistantSignOff || 'Kind regards,';
  const displayUserName = userName || '';
  const userFirstName = userName ? userName.split(' ')[0] : '';
  const userAccountEmail = selectedAccountEmail || '';

  // ── Signature card (shared across phases) ──────────────────────────────
  const signatureCard = (
    <div className='email-intro-right'>
      <div className='signature-card'>
        <div className='signature-content'>
          <p className='signature-closing'>{displaySignOff}</p>
          <p className='signature-spacer'>&nbsp;</p>
          <p className='signature-name'>{displayName}</p>
          <p className='signature-role'>
            Personal Assistant to {displayUserName}
          </p>
          {displayEmail && (
            <a className='signature-email' href={`mailto:${displayEmail}`}>
              {displayEmail}
            </a>
          )}
        </div>
      </div>
    </div>
  );

  // ── Failure path: email not available ─────────────────────────────────
  if (!emailAvailable) {
    return (
      <div className='onboarding-step email-intro-step'>
        <div className='email-intro-layout'>
          <div className='email-intro-left'>
            <h1 className='email-intro-title'>I can make tough calls</h1>
            <div className='email-intro-description'>
              <p>
                To you, I'm just {displayNickname}. When I speak to others, I'll
                introduce myself as <strong>{displayName}</strong>.
              </p>
              <p>
                My email's almost setup. I'll message you when it's ready so you
                can save my details.
              </p>
            </div>
            <div className='email-intro-actions'>
              <button className='step-button' onClick={handleNext}>
                Next
                <ArrowCircleRight />
              </button>
            </div>
          </div>
          {signatureCard}
        </div>
      </div>
    );
  }

  // ── Phase: initial — offer to send the intro email ─────────────────
  if (phase === 'initial') {
    const taskTitle = `Add new Task for ${displayNickname}`;
    const taskDesc = `Send a welcome email to ${userFirstName || 'me'} showing what you can do`;
    const pillText = `Recipient email: ${userAccountEmail}`;

    return (
      <div
        className={`onboarding-step email-intro-step${tutorialStep !== null ? ' tutorial-active' : ''}`}
      >
        <div
          className={`email-intro-layout${tutorialStep !== null ? ' tutorial-dimmed' : ''}`}
        >
          <div className='email-intro-left'>
            <h1 className='email-intro-title'>I can make tough calls</h1>
            <div className='email-intro-description'>
              <p>
                Or easy emails too. In fact I can make phone calls and send
                emails for lots of things, like bookings or finding services.
              </p>
              <ul>
                <li>
                  To you, I'm just {displayNickname}. When I speak to others,
                  I'll introduce myself as <strong>{displayName}</strong>.
                </li>
                <li>
                  Emails I send will come from my account:{' '}
                  <a className='email-link' href={`mailto:${displayEmail}`}>
                    {displayEmail}
                  </a>
                  .
                </li>
                <li>
                  If you need to contact someone as yourself, I can draft the
                  message — but you'll always be the one to send it.
                </li>
              </ul>
              <p>Why not try it now?</p>
            </div>
            <div className='email-intro-actions'>
              <button
                className='step-button'
                onClick={() => setTutorialStep(1)}
              >
                Get me to send an email
                <ArrowCircleRight />
              </button>
            </div>
          </div>
          {signatureCard}
        </div>

        {tutorialStep !== null && (
          <div className='task-tutorial-overlay'>
            <div className='task-tutorial-header'>
              <div className='task-tutorial-header-text'>
                <h2 className='task-tutorial-title'>
                  {TUTORIAL_STEPS[tutorialStep - 1].title}
                </h2>
                <p className='task-tutorial-desc'>
                  {TUTORIAL_STEPS[tutorialStep - 1].description}
                </p>
              </div>
              {tutorialStep < 5 && (
                <button
                  className='task-tutorial-next-btn'
                  onClick={() => setTutorialStep((s) => (s || 0) + 1)}
                >
                  <ArrowRightIcon />
                </button>
              )}
            </div>
            <div className='task-tutorial-task-card'>
              <div className='add-todo-modal'>
                <div className='modal-header'>
                  <h1 className='modal-title'>{taskTitle}</h1>
                </div>
                <div className='modal-body'>
                  <div
                    className={`todo-input-wrapper${
                      tutorialStep < 2 ? ' tutorial-hidden' : ''
                    }`}
                  >
                    <div
                      className={`todo-input${
                        tutorialStep === 2 ? ' tutorial-highlight' : ''
                      }`}
                      style={{ cursor: 'default' }}
                    >
                      {taskDesc}
                    </div>
                  </div>
                  <div className='questions-and-actions-row'>
                    <div
                      className={`question-pills-row${
                        tutorialStep < 3 ? ' tutorial-hidden' : ''
                      }`}
                    >
                      <span className='question-pill answered'>{pillText}</span>
                    </div>
                    <div
                      className={`action-bar${
                        tutorialStep < 4 ? ' tutorial-hidden' : ''
                      }`}
                    >
                      <div className='action-buttons-left' />
                      <div className='action-create-group'>
                        <div className='dropdown-wrapper'>
                          <div className='action-trigger'>
                            <ActionEmailSendIcon />
                            Email from {displayNickname}
                          </div>
                        </div>
                        <button
                          className={`create-btn${
                            tutorialStep < 5 ? ' tutorial-btn-hidden' : ''
                          }`}
                          onClick={handleSendEmail}
                          disabled={sending || tutorialStep < 5}
                        >
                          <PlusAddIcon />
                          {sending ? 'Creating…' : 'Create Task'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Phase: emailSent — offer to draft a reply ──────────────────────
  if (phase === 'emailSent') {
    return (
      <div className='onboarding-step email-intro-step'>
        <div className='email-intro-layout'>
          <div className='email-intro-left'>
            <h1 className='email-intro-title'>Let's try it out</h1>
            <div className='email-intro-description'>
              <p>
                🎉 Ok, that's sent. You should have a lovely email from me. If
                it's arrived in your junk folder then add me to your contacts.
              </p>
              <p>
                <strong>Now try getting me to draft an email.</strong>
              </p>
              <p>Remember, I won't send emails from your account.</p>
            </div>
            <div className='email-intro-actions'>
              <button
                className='step-button'
                onClick={() => setShowDraftModal(true)}
              >
                Ask me to draft an email
                <ArrowCircleRight />
              </button>
              <button className='skip-button' onClick={handleNext}>
                Skip
              </button>
            </div>
          </div>
          {signatureCard}
        </div>

        <OnboardingTaskModal
          isOpen={showDraftModal}
          onClose={() => setShowDraftModal(false)}
          onCreateTask={handleCreateDraft}
          title={`Add new Task for ${displayNickname}`}
          taskDescription={`Draft an email to ${displayNickname} sharing your details`}
          pills={[
            { label: 'From email address', value: userAccountEmail },
            { label: 'Recipient email', value: displayEmail },
          ]}
          actionLabel='Draft email from you'
          actionType='draft_email_from_user'
          submitting={drafting}
        />
      </div>
    );
  }

  // ── Phase: draftCreated — show Gmail link + Next ───────────────────
  return (
    <div className='onboarding-step email-intro-step'>
      <div className='email-intro-layout'>
        <div className='email-intro-left'>
          <h1 className='email-intro-title'>Let's try it out</h1>
          <div className='email-intro-description'>
            <p>
              Done, I've drafted your email. You can{' '}
              {gmailDraftLink ? (
                <a
                  className='email-link'
                  href={gmailDraftLink}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  view it in your Drafts in Gmail.
                </a>
              ) : (
                'view it in your Drafts in Gmail.'
              )}
            </p>
            <p>
              Use Drafts when you need to email someone as yourself, but need
              help writing it. I can also draft replies to emails in your inbox.
            </p>
          </div>
          <div className='email-intro-actions'>
            <button className='step-button' onClick={handleNext}>
              Next
              <ArrowCircleRight />
            </button>
          </div>
        </div>
        {signatureCard}
      </div>
    </div>
  );
};

export default EmailIntroStep;
